import type { ContractPeriod, DegreeDays, DjuSettings, EnergyKey, LngLat, PeriodKey, PlanBuilding, RatioSet, RatioTable, UsageKey, WeatherStation } from './types';
import { distanceKm } from './geo';
import { perfOf, perfTableOf } from './perf';

// ─── Référentiels (VALEURS DE DÉMONSTRATION — à fournir par le métier) ─────────

export const USAGES: { key: UsageKey; label: string }[] = [
  { key: 'resCollectif', label: 'Habitat collectif' },
  { key: 'resIndividuel', label: 'Habitat individuel' },
  { key: 'tertiaire', label: 'Tertiaire' },
  { key: 'commercial', label: 'Bâtiment commercial' },
  { key: 'enseignementPrimaire', label: 'Enseignement primaire' },
  { key: 'enseignementSuperieur', label: 'Enseignement secondaire et supérieur' },
  { key: 'sante', label: 'Santé' },
  { key: 'hotel', label: 'Hôtel' },
  { key: 'loisirs', label: 'Loisirs' },
  { key: 'piscine', label: 'Piscine' },
  { key: 'industrie', label: 'Industrie' },
];
export const LEGACY_USAGES: { key: UsageKey; label: string }[] = [
  { key: 'enseignement', label: 'Enseignement (ancienne catégorie non réaffectée)' },
  { key: 'autre', label: 'Autre (ancienne catégorie non réaffectée)' },
];
export const ALL_USAGES = [...USAGES, ...LEGACY_USAGES];

export const PERIODS: { key: PeriodKey; label: string }[] = [
  { key: 'avant1948', label: '< 1948' },
  { key: 'p1948_1974', label: '1948-1974' },
  { key: 'p1975_1999', label: '1975-1999' },
  { key: 'p2000_2012', label: '2000-2012' },
  { key: 'apres2012', label: '> 2012' },
];
export const CLIMATE_PERIODS: { key: PeriodKey; label: string }[] = [
  { key: 'avant2005', label: 'Très anciens (Avant 2005)' },
  { key: 'p2005_2012', label: 'Ancien 2005-2012' },
  { key: 'p2013_2020', label: 'Récent 2013-2020' },
  { key: 'apres2020', label: 'Neuf Supérieur à 2020' },
];
export const ALL_PERIODS = [...PERIODS, ...CLIMATE_PERIODS];
export const periodsOf = (r: RatioTable) => r.periodSchema === 'climate2026' ? CLIMATE_PERIODS : PERIODS;
export const referencePeriodOf = (r: RatioTable) => periodsOf(r)[0].key;
export const ratioPrecision = (r: RatioTable, value: number) => r.periodSchema === 'climate2026' ? value : Math.round(value * 10) / 10;

export const USAGE_LABEL = Object.fromEntries(ALL_USAGES.map((u) => [u.key, u.label])) as Record<UsageKey, string>;
export const PERIOD_LABEL = Object.fromEntries(ALL_PERIODS.map((p) => [p.key, p.label])) as Record<PeriodKey, string>;

/** Période de construction de référence : porte le ratio saisi et le coefficient 100 %. */
export const REF_PERIOD: PeriodKey = 'avant1948';

/** Postes d'un ratio : chauffage, eau chaude sanitaire, froid. */
export const ENERGIES: { key: EnergyKey; label: string; short: string; icon: string }[] = [
  { key: 'chauffage', label: 'Chauffage', short: 'Chauf.', icon: 'local_fire_department' },
  { key: 'ecs', label: 'Eau chaude sanitaire', short: 'ECS', icon: 'water_drop' },
  { key: 'froid', label: 'Froid', short: 'Froid', icon: 'ac_unit' },
];

/** DJ de référence des ratios de base : les ratios par défaut y sont ramenés par station. */
export const REF_DJU = 2500;
export const REF_DJCLIM = 110;

/** Besoins de chauffage de référence (kWh/m²/an) — valeurs de démonstration. */
const BASE_CHAUFFAGE: Partial<Record<UsageKey, Partial<Record<PeriodKey, number>>>> = {
  resCollectif: { avant1948: 165, p1948_1974: 148, p1975_1999: 119, p2000_2012: 89, apres2012: 48 },
  resIndividuel: { avant1948: 195, p1948_1974: 176, p1975_1999: 142, p2000_2012: 103, apres2012: 53 },
  tertiaire: { avant1948: 190, p1948_1974: 173, p1975_1999: 146, p2000_2012: 114, apres2012: 70 },
  enseignement: { avant1948: 155, p1948_1974: 140, p1975_1999: 118, p2000_2012: 93, apres2012: 56 },
  sante: { avant1948: 215, p1948_1974: 196, p1975_1999: 169, p2000_2012: 137, apres2012: 90 },
  autre: { avant1948: 138, p1948_1974: 124, p1975_1999: 104, p2000_2012: 83, apres2012: 50 },
};

/** ECS : peu dépendante de la période de construction. */
const BASE_ECS: Partial<Record<UsageKey, number>> = {
  resCollectif: 25, resIndividuel: 25, tertiaire: 10, enseignement: 8, sante: 45, autre: 8,
};

/** Froid : croissant avec la période de construction (taux d'équipement). */
const BASE_FROID: Partial<Record<UsageKey, number>> = {
  resCollectif: 4, resIndividuel: 2, tertiaire: 20, enseignement: 9, sante: 28, autre: 6,
};
const FROID_FACTOR: Partial<Record<PeriodKey, number>> = {
  avant1948: 0.6, p1948_1974: 0.7, p1975_1999: 1, p2000_2012: 1.3, apres2012: 1.6,
};

export const mapRatioTable = (cell: (u: UsageKey, p: PeriodKey) => RatioSet): Record<UsageKey, Record<PeriodKey, RatioSet>> =>
  Object.fromEntries(ALL_USAGES.map((u) => [
    u.key, Object.fromEntries(ALL_PERIODS.map((p) => [p.key, cell(u.key, p.key)])) as Record<PeriodKey, RatioSet>,
  ])) as Record<UsageKey, Record<PeriodKey, RatioSet>>;

const buildRatios = (heat: number, cool: number): RatioTable =>
  mapRatioTable((u, p) => {
    const heating = BASE_CHAUFFAGE[u];
    const cooling = BASE_FROID[u];
    const heatRatio = heating?.[p];
    const coolingFactor = FROID_FACTOR[p];
    return {
      chauffage: heatRatio === undefined ? null : Math.round(heatRatio * heat),
      ecs: coolingFactor === undefined ? null : BASE_ECS[u] ?? null,
      froid: cooling === undefined || coolingFactor === undefined ? null : Math.round(cooling * coolingFactor * cool),
    };
  });

/**
 * Ratios de référence (station aux DJ de référence) — valeurs de démonstration.
 * Le chauffage décroît d'une période à la suivante : les coefficients de performance
 * dérivés sont donc tous < 100 % et décroissants de gauche à droite.
 */
export const DEFAULT_RATIOS: RatioTable = buildRatios(1, 1);

/**
 * Ratios par défaut pour un couple de degrés-jours : chauffage au prorata des DJU,
 * froid au prorata des DJClim. FORMULE DE DÉMONSTRATION, à confirmer par le métier.
 */
export const ratiosForDegrees = (dju: number, djclim: number): RatioTable =>
  buildRatios(dju / REF_DJU, djclim / REF_DJCLIM);

export const ratiosForStation = (s: WeatherStation): RatioTable => ratiosForDegrees(s.dju, s.djclim);

export const ratioTotal = (r: RatioSet): number | null =>
  r.chauffage === null || r.ecs === null || r.froid === null ? null : r.chauffage + r.ecs + r.froid;

/** Ratio synthétique d'une typologie : période de construction de référence, poste par poste. */
export function usageRatio(t: RatioTable, u: UsageKey): RatioSet {
  return t[u][referencePeriodOf(t)];
}

/** Fixe le ratio de la période de référence : les autres périodes suivent leur coefficient. */
export function setUsageRatio(t: RatioTable, u: UsageKey, e: EnergyKey, target: number): RatioTable {
  const periods = { ...t[u], ...Object.fromEntries(periodsOf(t).map((p) => {
    const percent = perfOf(t, u, p.key, e);
    const v = percent === null ? null : ratioPrecision(t, target * (percent / 100));
    return [p.key, { ...t[u]?.[p.key], [e]: v }];
  })) } as RatioTable[UsageKey];
  return { ...t, performance: perfTableOf(t), [u]: periods };
}

/**
 * Calibre des ratios saisis pour un couple de degrés-jours sur un autre couple :
 * chauffage au prorata des DJU, froid au prorata des DJClim, ECS inchangée.
 * FORMULE DE DÉMONSTRATION, à confirmer par le métier.
 */
export function calibrateRatios(t: RatioTable, from: DegreeDays, to: DegreeDays): RatioTable {
  const kHeat = from.dju > 0 ? to.dju / from.dju : 1;
  const kCool = from.djclim > 0 ? to.djclim / from.djclim : 1;
  const r1 = (v: number | null, k: number) => v === null ? null : ratioPrecision(t, v * k);
  const normalized = cloneRatios(t);
  const ratios = mapRatioTable((u, p) => {
    const s = normalized[u][p];
    return { chauffage: r1(s.chauffage, kHeat), ecs: s.ecs, froid: r1(s.froid, kCool) };
  });
  return { ...ratios, periodSchema: t.periodSchema, climateZone: t.climateZone, performance: perfTableOf(t) };
}

/** Alimenté par les stations du jeu de démonstration préparé avant ouverture des parcours. */
export const STATIONS: WeatherStation[] = [];

/** Station la plus proche d'un point : proposition par défaut du projet. */
export function nearestStation(point: LngLat | null, stations: WeatherStation[] = STATIONS): WeatherStation {
  if (!stations.length) throw new Error('Aucune station source disponible');
  if (!point) return stations[0];
  return stations.reduce((best, s) => (distanceKm(point, s.position) < distanceKm(point, best.position) ? s : best), stations[0]);
}

export const DEFAULT_DJU: DjuSettings = { stationId: '', reference: REF_DJU };

export const RATIO_MIN = 0;
export const RATIO_MAX = 1000;
export const isValidRatio = (v: number) => Number.isFinite(v) && v >= RATIO_MIN && v <= RATIO_MAX;
export const isValidDju = (v: number) => Number.isFinite(v) && v >= 500 && v <= 5000;
export const isValidDjclim = (v: number) => Number.isFinite(v) && v >= 10 && v <= 1000;

export const cloneRatios = (r: RatioTable): RatioTable => ({
  ...mapRatioTable((u, p) => ({
    chauffage: r[u]?.[p]?.chauffage ?? null,
    ecs: r[u]?.[p]?.ecs ?? null,
    froid: r[u]?.[p]?.froid ?? null,
  })),
  performance: perfTableOf(r),
  periodSchema: r.periodSchema,
  climateZone: r.climateZone,
});

export const missingRatioCount = (r: RatioTable): number =>
  USAGES.reduce((count, u) => count + periodsOf(r).reduce((n, p) => n + ENERGIES.filter((e) =>
    r[u.key]?.[p.key]?.[e.key] == null || perfOf(r, u.key, p.key, e.key) === null).length, 0), 0);

/** Frontière MSW : ancien format partiel accepté, valeurs inconnues jamais converties en zéro. */
export function readRatioTable(value: unknown): RatioTable | null {
  const record = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
  if (!record(value) || !ALL_USAGES.some(u => u.key in value)) return null;
  const readMatrix = (input: Record<string, unknown>, coefficients: boolean): RatioTable | null => {
    let valid = true;
    const table = mapRatioTable((u, p) => {
      const usage = input[u];
      if (usage === undefined) return { chauffage: null, ecs: null, froid: null };
      const cell = record(usage) ? usage[p] : undefined;
      if (cell === undefined) return { chauffage: null, ecs: null, froid: null };
      const read = (e: EnergyKey): number | null => {
        const n = record(cell) ? cell[e] : undefined;
        if (n === null) return null;
        if (typeof n !== 'number' || !Number.isFinite(n) || n < 0
          || (coefficients && p === (value.periodSchema === 'climate2026' ? 'avant2005' : REF_PERIOD) && n !== 100)) { valid = false; return null; }
        return n;
      };
      return { chauffage: read('chauffage'), ecs: read('ecs'), froid: read('froid') };
    });
    return valid ? table : null;
  };
  const ratios = readMatrix(value, false);
  if (!ratios) return null;
  if (value.periodSchema !== undefined && value.periodSchema !== 'legacy' && value.periodSchema !== 'climate2026') return null;
  if (value.climateZone !== undefined && typeof value.climateZone !== 'string') return null;
  ratios.periodSchema = value.periodSchema;
  ratios.climateZone = value.climateZone;
  if (value.performance !== undefined) {
    if (!record(value.performance)) return null;
    const performance = readMatrix(value.performance, true);
    if (!performance) return null;
    ratios.performance = performance;
  }
  return cloneRatios(ratios);
}

/** Nombre de valeurs (usage × période × poste) qui s'écartent de la référence. */
export function countModifiedRatios(r: RatioTable, ref: RatioTable = DEFAULT_RATIOS): number {
  let n = r.periodSchema !== ref.periodSchema || r.climateZone !== ref.climateZone ? 1 : 0;
  for (const u of ALL_USAGES) {
    for (const p of ALL_PERIODS) {
      for (const e of ENERGIES) if ((r[u.key]?.[p.key]?.[e.key] ?? null) !== (ref[u.key]?.[p.key]?.[e.key] ?? null)
        || perfOf(r, u.key, p.key, e.key) !== perfOf(ref, u.key, p.key, e.key)) n += 1;
    }
  }
  return n;
}

export function periodOf(year: number, ratios?: RatioTable): PeriodKey {
  if (ratios?.periodSchema === 'climate2026') {
    if (year < 2005) return 'avant2005';
    if (year <= 2012) return 'p2005_2012';
    if (year <= 2020) return 'p2013_2020';
    return 'apres2020';
  }
  if (year < 1948) return 'avant1948';
  if (year <= 1974) return 'p1948_1974';
  if (year <= 1999) return 'p1975_1999';
  if (year <= 2012) return 'p2000_2012';
  return 'apres2012';
}

// ─── Calcul de consommation ───────────────────────────────────────────────

export function stationOf(dju: DjuSettings): WeatherStation {
  const station = STATIONS.find((s) => s.id === dju.stationId);
  if (!station) throw new Error(`Station source inconnue : ${dju.stationId}`);
  return station;
}

/**
 * Les ratios projet sont déjà calibrés par poste sur les DJU/DJClim retenus.
 * Aucun second facteur climatique ne doit être appliqué au total (ECS comprise).
 */
export function djuCoefficient(dju: DjuSettings): number {
  stationOf(dju);
  return 1;
}

export interface BuildingConsumption {
  ratio: number | null;
  /** MWh/an */
  raw: number | null;
  coefficient: number;
  /** MWh/an */
  corrected: number | null;
}

export const calculationPeriod = (b: PlanBuilding, ratios: RatioTable): PeriodKey | null =>
  ratios.periodSchema === 'climate2026'
    ? typeof b.year === 'number' && Number.isFinite(b.year) ? periodOf(b.year, ratios) : null
    : b.period;

export const canCalculateBuilding = (b: PlanBuilding, ratios?: RatioTable): boolean =>
  b.usage !== null && USAGES.some(u => u.key === b.usage)
  && (ratios ? calculationPeriod(b, ratios) !== null : b.period !== null) && b.heatedArea !== null;

export function buildingConsumption(b: PlanBuilding, ratios: RatioTable, dju: DjuSettings): BuildingConsumption {
  const coefficient = djuCoefficient(dju);
  const period = calculationPeriod(b, ratios);
  if (!canCalculateBuilding(b, ratios) || b.usage === null || period === null || b.heatedArea === null) {
    return { ratio: null, raw: null, coefficient, corrected: null };
  }
  const ratio = ratioTotal(ratios[b.usage][period]);
  if (ratio === null) return { ratio: null, raw: null, coefficient, corrected: null };
  const raw = (ratio * b.heatedArea) / 1000;
  return { ratio, raw, coefficient, corrected: raw * coefficient };
}

/** Consommation globale corrigée du périmètre, en MWh/an. */
export function totalConsumption(buildings: PlanBuilding[], ratios: RatioTable, dju: DjuSettings): number | null {
  let total = 0;
  for (const building of buildings) {
    const value = buildingConsumption(building, ratios, dju).corrected;
    if (value === null) return null;
    total += value;
  }
  return total;
}

/** Classes de la légende carte (MWh/an corrigés) — couleurs de jeu de données (hex autorisé). */
export const CONSUMPTION_CLASSES = [
  { max: 25, label: '< 25', color: '#fde6c8' },
  { max: 75, label: '25 – 75', color: '#f9bb7d' },
  { max: 200, label: '75 – 200', color: '#ef6b26' },
  { max: 500, label: '200 – 500', color: '#c2410c' },
  { max: Infinity, label: '≥ 500', color: '#7c2d12' },
] as const;

// ─── Géométrie ─────────────────────────────────────────────────────────────

export { boundsOf, centerOf, circlePolygon, closeRing, distanceKm, inPerimeter, pointInPolygon } from './geo';

// ─── Période de contrat ────────────────────────────────────────────────────

const MONTH_NAMES = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

/** `2027-04` → `avril 2027`. */
export function monthLabel(value: string): string {
  const [y, m] = value.split('-').map(Number);
  if (!y || !m || m < 1 || m > 12) return '—';
  return `${MONTH_NAMES[m - 1]} ${y}`;
}

const monthIndex = (value: string): number => {
  if (!/^\d{4,}-\d{2}$/.test(value)) return NaN;
  const [y, m] = value.split('-').map(Number);
  return Number.isFinite(y) && y > 0 && m >= 1 && m <= 12 ? y * 12 + (m - 1) : NaN;
};

/** Durée du contrat en mois, bornes incluses. 0 si la période est invalide. */
export function contractMonths(c: ContractPeriod): number {
  const a = monthIndex(c.start);
  const b = monthIndex(c.end);
  return Number.isFinite(a) && Number.isFinite(b) && b >= a ? b - a + 1 : 0;
}

export const isValidContract = (c: ContractPeriod): boolean => contractMonths(c) > 0;

/** `avril 2027 → mars 2047 · 20 ans`. Les durées non entières restent en mois. */
export function contractLabel(c: ContractPeriod): string {
  const months = contractMonths(c);
  if (months === 0) return 'Période à définir';
  const years = months / 12;
  const duration = Number.isInteger(years) ? `${years} an${years > 1 ? 's' : ''}` : `${months} mois`;
  return `${monthLabel(c.start)} → ${monthLabel(c.end)} · ${duration}`;
}

/** Période proposée par défaut : mois courant jusqu'au même mois dans 20 ans. */
export function defaultContract(from = new Date()): ContractPeriod {
  const start = new Date(from.getFullYear(), from.getMonth(), 1);
  const end = new Date(start.getFullYear() + 20, start.getMonth(), 1);
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  return { start: iso(start), end: iso(end) };
}

// ─── Formats ───────────────────────────────────────────────────────────────

export const fmt = (n: number | null, digits = 0) =>
  n === null || !Number.isFinite(n) ? 'Non disponible' : n.toLocaleString('fr-FR', { maximumFractionDigits: digits, minimumFractionDigits: digits });
export const gwh = (mwh: number | null) => mwh === null ? 'Non disponible' : `${fmt(mwh / 1000, 1)} GWh/an`;
export const today = () => new Date().toLocaleDateString('fr-FR');
